import { MarkdownSerializer, MarkdownSerializerState } from 'prosemirror-markdown'
import { Mark, Node } from 'prosemirror-model'
import { marks, nodes } from 'prose-core'

// Imported from https://github.com/ProseMirror/prosemirror-markdown/blob/3e5a5d02f7c1b9336744740149767ee1dc4e954d/src/to_markdown.ts
type MarkSerializerSpec = {
  open: string | ((state: MarkdownSerializerState, mark: Mark, parent: Node, index: number) => string)
  close: string | ((state: MarkdownSerializerState, mark: Mark, parent: Node, index: number) => string)
  mixable?: boolean
  expelEnclosingWhitespace?: boolean
  escape?: boolean
}

/* eslint-disable @typescript-eslint/no-unused-vars */
const nodeParsers: { [key in keyof typeof nodes]: (state: MarkdownSerializerState, node: Node) => void } = {
  ai_suggestion(state, node) {
    state.renderInline(node)
    state.closeBlock(node)
  },
  check_list(state, node) {
    state.renderList(node, '  ', () => '[] ')
  },
  code_block(state, node) {
    state.write('```\n')
    state.text(node.textContent, false)
    state.ensureNewLine()
    state.write('```')
    state.closeBlock(node)
  },
  heading(state, node) {
    state.write(state.repeat('#', node.attrs.level) + ' ')
    state.renderInline(node)
    state.closeBlock(node)
  },
  image(state, node) {
    state.write('![' + state.esc(node.attrs.description || '') + '](' + state.esc(node.attrs.src) + ')')
  },
  line_divider(state, node) {
    state.write('---')
    state.closeBlock(node)
  },
  list_item(state, node) {
    state.renderInline(node)
  },
  ordered_list(state, node) {
    state.renderList(node, '  ', (index) => {
      const identifiers = ['1', 'a', 'i']
      const child = node.child(index)
      const depth = child.attrs.depth || 0
      const identifier = identifiers[depth % 3]

      return state.repeat('  ', depth) + identifier + '. '
    })
  },
  paragraph(state, node) {
    state.renderInline(node)
    state.closeBlock(node)
  },
  text(state, node) {
    state.text(node.text || '')
  },
  unordered_list(state, node) {
    state.renderList(node, '  ', () => '- ')
  },
  vote_list(state, node) {
    state.renderList(node, '  ', () => '- ')
  },
  vote_list_item(state, node) {
    state.renderInline(node)
  },
  // not yet implemented, but needed so the serializer doesn't crash
  box(state, node) {
    return
  },
  br(state, node) {
    return
  },
  column_group(state, node) {
    return
  },
  column(state, node) {
    return
  },
  smartLink(state, node) {
    return
  },
  table_cell(state, node) {
    return
  },
  table_header(state, node) {
    return
  },
  table_row(state, node) {
    return
  },
  table(state, node) {
    return
  },
  toggle_list(state, node) {
    return
  },
  // not needed for the serializer
  asana_embed(state, node) {
    return
  },
  card(state, node) {
    return
  },
  collection(state, node) {
    return
  },
  doc(state, node) {
    return
  },
  download(state, node) {
    return
  },
  generate_image(state, node) {
    return
  },
  github_embed(state, node) {
    return
  },
  iframe(state, node) {
    return
  },
  jira_embed(state, node) {
    return
  },
  mermaid_embed(state, node) {
    return
  },
  summary(state, node) {
    return
  },
  table_of_contents(state, node) {
    return
  },
}

const markParsers: { [key in keyof typeof marks]: MarkSerializerSpec } = {
  code: { open: '`', close: '`' },
  em: { open: '*', close: '*', mixable: true },
  link: {
    open: '[',
    close(_, mark) {
      return (
        '](' +
        mark.attrs.href.replace(/[()"]/g, '\\$&') +
        (mark.attrs.title ? ` "${mark.attrs.title.replace(/"/g, '\\"')}"` : '') +
        ')'
      )
    },
  },
  strike: { open: '~~', close: '~~', mixable: true },
  strong: { open: '**', close: '**', mixable: true },
  subscript: { open: '~', close: '~', mixable: true },
  superscript: { open: '^', close: '^', mixable: true },
  underline: { open: '__', close: '__', mixable: true },
  // not implemented, nor needed/supported in markdown at this point
  annotate_comment: { open: '', close: '' },
  annotate_todo: { open: '', close: '' },
  colored: { open: '', close: '' },
  comment: { open: '', close: '' },
  deletion: { open: '', close: '' },
  font_family: { open: '', close: '' },
  font_size: { open: '', close: '' },
  font_weight: { open: '', close: '' },
  formatting_change: { open: '', close: '' },
  highlighted: { open: '', close: '' },
  insertion: { open: '', close: '' },
  review: { open: '', close: '' },
  task: { open: '', close: '' },
  ai_revision: { open: '', close: '' },
}

export const markdownSerializer = new MarkdownSerializer(nodeParsers, markParsers)
/* eslint-enable @typescript-eslint/no-unused-vars */
